// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminAdManager\AdminAdCampaignDetail.jsx
// Название: Admin Ad Campaign Detail
// Версия: 1.0
// Дата: 18.02.2026

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AdminAdManager.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com/api';

const AdminAdCampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ============================================
  // СЕКЦИЯ: Load data
  // ============================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/ad-manager/campaigns/${id}`, { headers });
      const data = await res.json();
      if (data.success) {
        setCampaign(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // СЕКЦИЯ: Actions
  // ============================================
  const handleToggleUnit = async (unitId, action) => {
    try {
      const res = await fetch(`${API_URL}/ad-manager/units/${unitId}/${action}`, {
        method: 'POST', headers
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Unit ${action}d`);
        await loadData();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleCampaign = async (action) => {
    try {
      const res = await fetch(`${API_URL}/ad-manager/campaigns/${id}/${action}`, {
        method: 'POST', headers
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Campaign ${action}d`);
        await loadData();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================
  // СЕКЦИЯ: Helpers
  // ============================================
  const getStatusBadge = (status) => {
    const map = {
      active: { class: 'status-active', label: '🟢 Active' },
      paused: { class: 'status-paused', label: '⏸ Paused' },
      redeemed: { class: 'status-completed', label: '✅ Redeemed' },
      completed: { class: 'status-completed', label: '✅ Completed' },
      creating: { class: 'status-creating', label: '⏳ Creating' },
      ready: { class: 'status-draft', label: '📝 Ready' },
      expired: { class: 'status-error', label: '⌛ Expired' },
      error: { class: 'status-error', label: '❌ Error' }
    };
    return map[status] || { class: '', label: status };
  };

  const formatEur = (amount) => `€${(parseFloat(amount) || 0).toFixed(2)}`;

  const getPauseReasonLabel = (reason) => {
    const map = {
      redeemed: '✅ Gift activated by user',
      overspend: '💰 Budget limit reached',
      timeout: '⏰ Time limit reached',
      manual: '✋ Manually paused',
      campaign_completed: '🏁 Campaign completed'
    };
    return map[reason] || reason;
  };

  // ============================================
  // СЕКЦИЯ: Render
  // ============================================
  if (loading) {
    return <div className="adm-container"><div className="adm-loading">Loading campaign details...</div></div>;
  }

  if (error && !campaign) {
    return (
      <div className="adm-container">
        <div className="adm-msg adm-msg-error">❌ {error}</div>
        <button className="adm-btn adm-btn-secondary" onClick={() => navigate('/admin/ad-manager')}>← Back</button>
      </div>
    );
  }

  const units = campaign?.creative_units || [];
  const activeUnits = units.filter(u => u.status === 'active').length;
  const redeemedUnits = units.filter(u => u.status === 'redeemed').length;
  const pausedUnits = units.filter(u => u.status === 'paused').length;
  const ctr = campaign.total_impressions > 0
    ? ((campaign.total_clicks / campaign.total_impressions) * 100).toFixed(2)
    : '0.00';
  const costPerRedeem = redeemedUnits > 0
    ? (parseFloat(campaign.total_spent_eur) / redeemedUnits).toFixed(2)
    : '-';

  return (
    <div className="adm-container">
      {/* Back */}
      <div className="adm-detail-back">
        <button className="adm-btn adm-btn-secondary" onClick={() => navigate('/admin/ad-manager')}>
          ← Back to Ad Manager
        </button>
      </div>

      {/* Messages */}
      {error && <div className="adm-msg adm-msg-error">❌ {error} <button onClick={() => setError(null)}>✕</button></div>}
      {successMsg && <div className="adm-msg adm-msg-success">✅ {successMsg} <button onClick={() => setSuccessMsg(null)}>✕</button></div>}

      {/* Header */}
      <div className="adm-header">
        <div>
          <h1>📢 {campaign.campaign_name || `Ad Campaign #${campaign.id}`}</h1>
          <p className="adm-subtitle">
            {campaign.brand_name && `${campaign.brand_name} · `}
            {campaign.platform === 'google' ? '🔵 Google Ads' : campaign.platform}
            {' · '}
            <span className={`adm-status ${getStatusBadge(campaign.status).class}`}>
              {getStatusBadge(campaign.status).label}
            </span>
          </p>
        </div>
        <div className="adm-header-actions">
          {campaign.status === 'active' && (
            <button className="adm-btn adm-btn-warn" onClick={() => handleToggleCampaign('pause')}>
              ⏸ Pause All
            </button>
          )}
          {campaign.status === 'paused' && (
            <button className="adm-btn adm-btn-success" onClick={() => handleToggleCampaign('resume')}>
              ▶ Resume All
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="adm-detail-stats">
        <div className="adm-stat-card">
          <span className="stat-value">{units.length}</span>
          <span className="stat-label">Total Ads</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value adm-stat-green">{activeUnits}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value" style={{color: '#28a745'}}>{redeemedUnits}</span>
          <span className="stat-label">Redeemed</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value" style={{color: '#ffc107'}}>{pausedUnits}</span>
          <span className="stat-label">Paused</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{(campaign.total_impressions || 0).toLocaleString()}</span>
          <span className="stat-label">Impressions</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{(campaign.total_clicks || 0).toLocaleString()}</span>
          <span className="stat-label">Clicks</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{ctr}%</span>
          <span className="stat-label">CTR</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{formatEur(campaign.total_spent_eur)}</span>
          <span className="stat-label">Total Spent</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{formatEur(campaign.daily_budget_eur)}</span>
          <span className="stat-label">Daily Budget</span>
        </div>
        <div className="adm-stat-card">
          <span className="stat-value">{costPerRedeem === '-' ? '-' : `€${costPerRedeem}`}</span>
          <span className="stat-label">Cost / Redeem</span>
        </div>
      </div>

      {/* Creative Units */}
      <section className="adm-section">
        <h2>🎨 Creative Units ({units.length})</h2>
        {units.length === 0 ? (
          <div className="adm-empty"><p>No creative units</p></div>
        ) : (
          units.map(unit => {
            const st = getStatusBadge(unit.status);
            return (
              <div key={unit.id} className="adm-unit-card">
                {/* Image */}
                {unit.image_url && (
                  <img src={unit.image_url} alt={`Gift #${unit.brand_gift_id}`} className="adm-unit-image" />
                )}

                {/* Info */}
                <div className="adm-unit-info">
                  <h4>
                    Gift #{unit.brand_gift_id}
                    {' '}
                    <span className={`adm-status ${st.class}`}>{st.label}</span>
                  </h4>
                  <p className="adm-unit-meta">
                    Ad ID: {unit.external_ad_id || 'pending'}
                    {' · '}
                    Landing: <a href={unit.landing_url} target="_blank" rel="noopener noreferrer">{unit.landing_slug}</a>
                  </p>
                  {unit.pause_reason && (
                    <p className="adm-unit-meta">{getPauseReasonLabel(unit.pause_reason)}</p>
                  )}
                  {unit.redeemed_at && (
                    <p className="adm-unit-meta">Redeemed: {new Date(unit.redeemed_at).toLocaleString()}</p>
                  )}

                  {/* Metrics */}
                  <div className="adm-unit-metrics">
                    <span className="adm-unit-metric">👁 <strong>{unit.impressions || 0}</strong> imp</span>
                    <span className="adm-unit-metric">👆 <strong>{unit.clicks || 0}</strong> clicks</span>
                    <span className="adm-unit-metric">📥 <strong>{unit.downloads || 0}</strong> downloads</span>
                    <span className="adm-unit-metric">💰 <strong>{formatEur(unit.spend_eur)}</strong> / {formatEur(unit.max_spend_eur)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="adm-unit-actions">
                  {unit.status === 'active' && (
                    <button className="adm-btn adm-btn-sm adm-btn-warn" onClick={() => handleToggleUnit(unit.id, 'pause')}>
                      ⏸ Pause
                    </button>
                  )}
                  {unit.status === 'paused' && (
                    <button className="adm-btn adm-btn-sm adm-btn-success" onClick={() => handleToggleUnit(unit.id, 'resume')}>
                      ▶ Resume
                    </button>
                  )}
                  {unit.landing_url && (
                    <a href={unit.landing_url} target="_blank" rel="noopener noreferrer" className="adm-btn adm-btn-sm adm-btn-secondary">
                      🔗 Landing
                    </a>
                  )}
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* Campaign Info */}
      <section className="adm-section">
        <h2>ℹ️ Campaign Info</h2>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <tbody>
              <tr><td><strong>DARY Campaign</strong></td><td>#{campaign.campaign_id} — {campaign.campaign_name}</td></tr>
              <tr><td><strong>External Campaign ID</strong></td><td>{campaign.external_campaign_id || '-'}</td></tr>
              <tr><td><strong>External Ad Group ID</strong></td><td>{campaign.external_adgroup_id || '-'}</td></tr>
              <tr><td><strong>Max Spend per Ad</strong></td><td>{formatEur(campaign.max_spend_per_ad_eur)}</td></tr>
              <tr><td><strong>Max Hours per Ad</strong></td><td>{campaign.max_hours_per_ad || 6}h</td></tr>
              <tr><td><strong>Targeting</strong></td><td>{JSON.stringify(campaign.targeting_config || {})}</td></tr>
              <tr><td><strong>Started</strong></td><td>{campaign.started_at ? new Date(campaign.started_at).toLocaleString() : '-'}</td></tr>
              <tr><td><strong>Last Sync</strong></td><td>{campaign.last_sync_at ? new Date(campaign.last_sync_at).toLocaleString() : 'Never'}</td></tr>
              {campaign.last_error && <tr><td><strong>Last Error</strong></td><td className="adm-stat-red">{campaign.last_error}</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminAdCampaignDetail;